import type { PiniaPluginContext } from 'pinia'
import { get as idbGet, set as idbSet, del as idbDel } from 'idb-keyval'

const PERSIST_PREFIX = 'bzuco:'
const LEGACY_KEY = 'vuex'

interface PersistOptions {
    ids: readonly string[]
}

export interface LegacyVuexState {
    events?: unknown
    zones?: unknown
    checkpoints?: unknown
    activeEvents?: unknown
    activeCheckpoint?: unknown
    activeZones?: unknown
    zoneStats?: unknown
    tickets?: unknown
    lastZones?: unknown
    lastZoneLoadDate?: unknown
    sendQueue?: unknown
    settings?: unknown
}

let migrationDone = false

async function tryLegacyMigration(): Promise<LegacyVuexState | null> {
    if (migrationDone) return null
    migrationDone = true
    try {
        const raw = localStorage.getItem(LEGACY_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw) as LegacyVuexState
        localStorage.removeItem(LEGACY_KEY)
        return parsed
    } catch {
        return null
    }
}

export function createPersistPlugin(options: PersistOptions) {
    const persistedIds = new Set(options.ids)

    return async (context: PiniaPluginContext) => {
        const { store } = context
        if (!persistedIds.has(store.$id)) return

        const key = PERSIST_PREFIX + store.$id
        const saved = await idbGet<unknown>(key)
        if (saved && typeof saved === 'object') {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            store.$patch(saved as any)
        }

        let timer: number | undefined
        store.$subscribe((_mutation, state) => {
            if (timer !== undefined) window.clearTimeout(timer)
            timer = window.setTimeout(() => {
                idbSet(key, JSON.parse(JSON.stringify(state))).catch(err => {
                    console.error('persist failed', store.$id, err)
                })
            }, 300)
        })
    }
}

export async function migrateLegacyVuex(): Promise<LegacyVuexState | null> {
    return tryLegacyMigration()
}

export async function clearAllPersisted(ids: readonly string[]): Promise<void> {
    await Promise.all(ids.map(id => idbDel(PERSIST_PREFIX + id)))
}
